import {
  BodySmall,
  ChevronRightIcon,
  HeadingSmall,
  ChoiceIcon,
  QuestionIcon,
  TeamIcon,
  Tooltip,
  theme,
} from '@arnold/common';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { NewReportData, QuestionSummary } from '../types';
import { isMultipleChoice } from '@arnold/core';
import { AnswerType } from '../../../lib/reports/question';

type Question = NewReportData['questions'][number];

interface SelectCardProps {
  summary?: QuestionSummary;
  eligibleCount: number;
  question: Question;
  onDetailClick?: () => void;
}

export const SelectCard = ({ summary, eligibleCount, question, onDetailClick }: SelectCardProps) => {
  const { t } = useTranslation('newReport');
  const options =
    summary?.choices ??
    (question.answersLocal ?? []).map((label, index) => ({ label, index, count: 0, percentage: 0 }));
  const answeredCount = summary?.respondingChannelIds.size ?? 0;
  const responseRate = eligibleCount ? Math.round((100 * answeredCount) / eligibleCount) : 0;
  const isMultiselect = isMultipleChoice(question);

  return (
    <CardWrapper data-cy="select-card">
      <HeaderContainer>
        <IconBadge>
          <QuestionIcon />
        </IconBadge>
        <Header>
          <HeadingSmall margin="0">{question.text}</HeadingSmall>
          <Subtext>
            <span>{isMultiselect ? t('selectCard.multipleChoiceType') : t('selectCard.singleChoiceType')}</span>
            <IconText>
              <TeamIcon />
              {t('selectCard.responseCount', { count: answeredCount, postProcess: 'interval' })}
            </IconText>
            <IconText>
              <ChoiceIcon />
              {t('selectCard.responseRate', { rate: responseRate })}
            </IconText>
          </Subtext>
        </Header>
        {onDetailClick && (
          <DetailButton
            type="button"
            onClick={onDetailClick}
            aria-label={t('selectCard.detailButtonLabel')}
            data-cy="select-card-detail-button"
          >
            <ChevronRightIcon />
          </DetailButton>
        )}
      </HeaderContainer>
      <AnswerList>
        {options.map((option) => {
          let answerHighlightType: AnswerType | undefined;
          const highlightLevel = (question.answersToHighlight || []).reduce(
            (accumulator: number, indexOrString: string | number) => {
              const optionIndex =
                typeof indexOrString === 'string' ? parseInt(indexOrString as string, 10) : Number(indexOrString);
              return accumulator + (optionIndex === Number(option.index) ? 1 : 0);
            },
            0,
          );
          if (highlightLevel >= 2) {
            answerHighlightType = AnswerType.ALERT;
          } else if (highlightLevel === 1) {
            answerHighlightType = AnswerType.WARNING;
          }
          return (
            <AnswerRow key={option.index}>
              <AnswerLabel color={theme.colors.text.secondary} ellipsis={1}>
                <Tooltip title={option.label} position="top">
                  <span>{option.label}</span>
                </Tooltip>
              </AnswerLabel>
              <BarTrack>
                <Bar style={{ width: `${option.percentage}%` }} answerHighlightType={answerHighlightType} />
                <AnswerValue>
                  {Math.round(option.percentage)}% <AnswerCount>({option.count})</AnswerCount>
                </AnswerValue>
              </BarTrack>
            </AnswerRow>
          );
        })}
      </AnswerList>
    </CardWrapper>
  );
};

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${theme.colors.backgroundCover.default};
  border-radius: ${theme.borderRadius.l};
  border: 1px solid ${theme.colors.borderSeparator.default};
  margin-bottom: ${theme.spacing.d};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
`;

const IconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.m};
  background-color: ${theme.colors.emotionInformative.background};
`;

const Subtext = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${theme.spacing.f};
  font-family: 'Roboto', sans-serif;
  font-size: ${theme.typography.body.small.regular.fontSize};
  color: ${theme.colors.text.secondary};
  margin-top: ${theme.spacing.e};
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.e};
  padding: ${theme.spacing.g};
  padding-left: 76px;
  padding-right: 36px;
`;

const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
`;

const AnswerLabel = styled(BodySmall)`
  flex: 0 0 20%;
  width: 20%;
  text-align: right;
`;

const BarTrack = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
`;

const Bar = styled.div<{ answerHighlightType?: AnswerType }>`
  flex: 0 1 auto;
  min-width: 2px;
  height: 26px;
  border-radius: ${theme.borderRadius.xs};
  background-color: ${theme.colors.chart.neutral[0]};
  background-color: ${({ answerHighlightType }) =>
    answerHighlightType === AnswerType.ALERT
      ? theme.colors.chart.alert[0]
      : answerHighlightType === AnswerType.WARNING
        ? theme.colors.chart.warning[0]
        : theme.colors.chart.neutral[0]};
`;

const AnswerValue = styled.span`
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: ${theme.spacing.d};
  font-family: 'Roboto', sans-serif;
  font-size: ${theme.typography.body.small.regular.fontSize};
  font-weight: ${theme.typography.body.small.bold.fontWeight};
  color: ${theme.colors.text.primary};
`;

const AnswerCount = styled.span`
  font-weight: ${theme.typography.body.small.regular.fontWeight};
  color: ${theme.colors.text.disabled};
`;

const IconText = styled.span`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.c};
`;

const HeaderContainer = styled.div`
  border-bottom: 1px solid ${theme.colors.borderSeparator.default};
  padding: ${theme.spacing.g};
  display: flex;
  gap: ${theme.spacing.f};
`;

const DetailButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid ${theme.colors.brand.primary};
  background-color: transparent;
  color: ${theme.colors.brand.primary};
  cursor: pointer;
  transition:
    background-color 0.15s ease-in-out,
    color 0.15s ease-in-out;

  svg {
    width: 16px;
    height: 16px;
  }

  svg path {
    fill: currentColor;
  }

  &:hover {
    background-color: ${theme.colors.brand.primary};
    color: ${theme.colors.textInverted.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.actionPrimary.focused};
    outline-offset: 2px;
  }
`;
